import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PrimaryRail } from './PrimaryRail';
import { SecondaryPanel } from './SecondaryPanel';
import { CanvasHeader } from './CanvasHeader';
import { FloatingSearchDock } from './FloatingSearchDock';
import { NotificationsDrawer } from './NotificationsDrawer';
import { AISpotlight } from './AISpotlight';
import { AIFab } from './AIFab';
import { useHotkey } from '@/hooks/useHotkeys';

/*
  AppShell — корневой layout v3, с auto-rail-collapse поведением.

  Derived state из route (зафиксировано 2026-05-21):
  - Home (/) → НЕТ secondary panel, rail EXPANDED (240px с labels)
  - Marketplace/Portfolio/Settings/etc → ЕСТЬ secondary panel,
    rail AUTO-COLLAPSE до 80px чтобы дать место

  Manual override:
  - Юзер может через chevron-toggle в rail-bottom развернуть/свернуть
    rail вручную. Это override держится пока юзер не перейдёт на другой
    route (тогда сбрасываем к auto-derived).

  Hotkeys:
    ⌘K / Ctrl+K — toggle search dock
    ⌘J / Ctrl+J — toggle AI Spotlight
    ⌘B / Ctrl+B — toggle secondary panel collapsed
    Escape      — close (priority: spotlight → search → notif)
*/

export function AppShell() {
  const location = useLocation();

  // Route → has secondary panel?
  const hasSecondary = routeHasSecondaryPanel(location.pathname);

  // Sprint 1.18: rail-state с tri-source tracking.
  // Three values: null (expanded) | 'manual' (юзер toggle) | 'auto' (secondary commit).
  // Это позволяет правильно реагировать на click-X (Home):
  //   - collapseSource === 'auto'   → click-X expands (юзер не выбирал коллапс)
  //   - collapseSource === 'manual' → click-X НЕ меняет (юзер хотел collapsed)
  //   - collapseSource === null     → already expanded, no-op
  // Sprint 1.17 был `boolean isRailCollapsed` — потерян источник коллапса.
  const [collapseSource, setCollapseSource] = useState<'manual' | 'auto' | null>(null);
  const railExpanded = collapseSource === null;

  // Sprint 1.18: при переходе на `/` (Home) — если rail сжат auto'м
  // (secondary commit), раскрываем обратно. Manual коллапс уважаем — юзер
  // явно хотел compact mode.
  const [previousPath, setPreviousPath] = useState(location.pathname);
  if (previousPath !== location.pathname) {
    setPreviousPath(location.pathname);
    if (location.pathname === '/' && collapseSource === 'auto') {
      setCollapseSource(null);
    }
  }

  const [secondaryCollapsed, setSecondaryCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  // Hotkeys
  useHotkey('mod+k', (e) => {
    e.preventDefault();
    setSearchOpen((v) => !v);
    setAiOpen(false);
    setNotifOpen(false);
  });
  useHotkey('mod+j', (e) => {
    e.preventDefault();
    setAiOpen((v) => !v);
    setSearchOpen(false);
    setNotifOpen(false);
  });
  useHotkey(
    'mod+b',
    (e) => {
      if (!hasSecondary) return;
      e.preventDefault();
      setSecondaryCollapsed((v) => !v);
    },
    [hasSecondary],
  );
  useHotkey(
    'Escape',
    () => {
      if (aiOpen) setAiOpen(false);
      else if (searchOpen) setSearchOpen(false);
      else if (notifOpen) setNotifOpen(false);
    },
    [aiOpen, searchOpen, notifOpen],
  );

  return (
    <div className="flex h-screen w-screen items-stretch gap-3 overflow-hidden bg-frame p-3">
      <PrimaryRail
        expanded={railExpanded}
        onToggleExpand={() => {
          // Manual toggle: expanded → manual-collapsed, или collapsed (any) → expanded
          setCollapseSource((prev) => (prev === null ? 'manual' : null));
        }}
        onOpenAISpotlight={() => {
          setAiOpen(true);
          setSearchOpen(false);
        }}
        onToggleNotifications={() => {
          // Toggle: повторный клик на bell закрывает drawer
          setNotifOpen((v) => !v);
          setSearchOpen(false);
          setAiOpen(false);
        }}
      />

      <SecondaryPanel
        visible={hasSecondary}
        collapsed={secondaryCollapsed}
        // Sprint 1.18: terminal-select forces 'auto'-collapse. Это позволяет
        // click-X (Home) обратно раскрыть rail. Manual коллапс не перетираем
        // (если юзер уже был manual-collapsed, секондари не имеет права
        // переписать его intent).
        onTerminalSelect={() => {
          setCollapseSource((prev) => (prev === 'manual' ? prev : 'auto'));
        }}
      />

      <main
        className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-canvas shadow-inner-card"
        style={{ viewTransitionName: 'canvas-content' }}
      >
        <CanvasHeader />
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      {/* Floating layer */}
      <AIFab onOpen={() => setAiOpen(true)} />
      <FloatingSearchDock
        expanded={searchOpen}
        onOpen={() => setSearchOpen(true)}
        onClose={() => setSearchOpen(false)}
      />
      <NotificationsDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <AISpotlight open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}

// ----- Helpers -----

/*
  routeHasSecondaryPanel — на каких route'ах показываем secondary nav.
  Home (/) — нет secondary, шире canvas.
  Все остальные базовые модули — есть.
*/
function routeHasSecondaryPanel(path: string): boolean {
  if (path === '/') return false; // Home — широкий canvas, без secondary
  if (path.startsWith('/marketplace')) return true;
  if (path.startsWith('/portfolio')) return true;
  if (path.startsWith('/settings')) return true;
  return false;
}
